import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { trackModalView } from '../../../utils/tracker';

interface ModalProps {
  isOpen?: boolean;
  open?: boolean; // Legacy compat
  onClose: () => void;
  title?: React.ReactNode | string;
  subTitle?: string; // Legacy compat
  children: React.ReactNode;
  footer?: React.ReactNode | (() => React.ReactNode); // Legacy compat
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
  className?: string;
  overrideStyle?: React.CSSProperties; // Legacy compat
}

const Modal = ({
  isOpen,
  open,
  onClose,
  title,
  subTitle,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  className = '',
  overrideStyle,
}: ModalProps) => {
  const actualIsOpen = isOpen ?? open ?? false;
  const [mounted, setMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Ensure component is client-mounted for Portals
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (actualIsOpen) {
      if (typeof title === 'string') trackModalView(title);
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      // Allows the out-animation to play before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        document.body.style.overflow = '';
      }, 200); // 200ms matches Tailwind duration-200
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [actualIsOpen, shouldRender, title]);

  // Listen for escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && actualIsOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [actualIsOpen, onClose]);

  const isAnimatingOut = !actualIsOpen && shouldRender;

  if (!mounted || !shouldRender) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'max-w-md w-full',
    md: 'max-w-2xl w-full',
    lg: 'max-w-4xl w-full',
    xl: 'max-w-6xl w-full',
    full: 'max-w-[95vw] w-full',
  };

  const renderFooter = () => {
    if (!footer) return null;
    return typeof footer === 'function' ? footer() : footer;
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 
                ${!actualIsOpen && isAnimatingOut ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'}`}
    >
      {/* Backdrop Overlay */}
      <div
        className='absolute inset-0 bg-neutral-900/60 backdrop-blur-[2px]'
        onClick={closeOnOverlayClick ? handleOverlayClick : undefined}
      />

      {/* Modal Dialog */}
      <div
        ref={modalRef}
        style={overrideStyle}
        className={`relative bg-white rounded-md shadow-2xl flex flex-col max-h-[90vh] overflow-hidden ${sizeClasses[size]} ${className} 
                ${!actualIsOpen && isAnimatingOut ? 'animate-out zoom-out-95 duration-200' : 'animate-in zoom-in-95 duration-200'}`}
      >
        {/* Header */}
        {title && (
          <div className='flex items-start justify-between px-6 py-4 border-b border-neutral-100 bg-white z-10 shrink-0'>
            <div className='flex flex-col'>
              <div className='text-lg font-bold tracking-tight text-neutral-800 capitalize'>
                {title}
              </div>
              {subTitle && (
                <div className='text-sm text-neutral-500 mt-1'>
                  {subTitle}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className='p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0898a0]/20'
              aria-label='Close modal'
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>
        )}

        {!title && (
          <button
            onClick={onClose}
            className='absolute top-4 right-4 z-20 p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors bg-white/50 backdrop-blur-sm'
            aria-label='Close modal'
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        )}

        {/* Body - Scrollable content */}
        <div className='overflow-y-auto overflow-x-hidden flex-1 custom-scrollbar'>
          <div className='p-6'>{children}</div>
        </div>

        {/* Footer */}
        {footer && (
          <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/50 shrink-0 z-10'>
            {renderFooter()}
          </div>
        )}
      </div>
    </div>
  );

  // Fallback to body if modal-root doesn't exist
  const root = document.getElementById('modal-root') || document.body;
  return createPortal(modalContent, root);
};

export default Modal;
